<template>
  <div class="admin-page conversion-reports">
    <admin-page-header
      eyebrow="Finans"
      title="Conversionlar"
      description="Tüm conversion kayıtlarını, tarihsel hakediş dağılımını ve network durumunu inceleyin."
    >
      <template #actions>
        <v-btn
          outlined
          small
          color="primary"
          :loading="loading"
          @click="fetchRows"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>
    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />
    <v-card
      outlined
      class="admin-table-card mt-4"
    >
      <v-form
        class="pa-4"
        @submit.prevent="applyFilters"
      >
        <v-row dense>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-text-field
              v-model.trim="filters.search"
              outlined
              dense
              hide-details
              clearable
              label="Kullanıcı ID, ad, e-posta, link veya işlem ID"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
            lg="2"
          >
            <v-combobox
              v-model="filters.network"
              :items="networks"
              outlined
              dense
              hide-details
              clearable
              label="Network"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
            lg="2"
          >
            <v-select
              v-model="filters.status"
              :items="statuses"
              outlined
              dense
              hide-details
              label="Conversion durumu"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
            lg="2"
          >
            <v-select
              v-model="filters.observedStatus"
              :items="networkStatuses"
              outlined
              dense
              hide-details
              clearable
              label="Network durumu"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
            lg="2"
          >
            <v-select
              v-model="filters.tier"
              :items="tiers"
              outlined
              dense
              hide-details
              clearable
              label="İşlemdeki paket"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
          >
            <v-text-field
              v-model="filters.from"
              type="date"
              outlined
              dense
              hide-details
              label="Başlangıç (Türkiye)"
            />
          </v-col>
          <v-col
            cols="6"
            md="3"
          >
            <v-text-field
              v-model="filters.to"
              type="date"
              outlined
              dense
              hide-details
              label="Bitiş (Türkiye)"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-checkbox
              v-model="filters.hideZeroNetworkAmount"
              dense
              hide-details
              label="Brüt network tutarı 0 olanları gizle"
              @change="applyFilters"
            />
          </v-col>
          <v-col
            cols="12"
            class="d-flex align-center justify-end"
          >
            <v-btn
              text
              small
              @click="resetFilters"
            >
              Temizle
            </v-btn><v-btn
              color="primary"
              small
              type="submit"
              :loading="loading"
            >
              Filtrele
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <v-alert
        v-if="error"
        outlined
        dense
        type="error"
        class="mx-4"
        role="alert"
      >
        {{ error }} <v-btn
          text
          small
          @click="fetchRows"
        >
          Tekrar dene
        </v-btn>
      </v-alert>
      <div class="px-4 pb-4 text-caption text--secondary">
        {{ formatNumber(total) }} kayıt listeleniyor; conversion sayısı yalnız bekleyen + kesinleşen kayıtları kapsar.
        {{ formatNumber(summary.cancelledCount) }} iptal kaydı listede korunur; iptaller finansal toplamlara dahil edilmez.
        Adverport kârı = brüt network tutarı − kullanıcı hakedişi; vergi ve giderler düşülmemiştir, tahsil edilmiş kâr değildir.
        Paket ve oran işlemin tarihsel kaydıdır, kullanıcının bugünkü paketi değildir.
      </div>
      <v-alert
        v-if="summary.missingAllocationCount"
        outlined
        dense
        type="warning"
        class="mx-4"
      >
        İptal edilmemiş {{ formatNumber(summary.missingAllocationCount) }} kayıtta dağılım eksik veya belirsiz. Eksik tutarlar sıfır kabul edilmez; hakediş/kâr toplamları yalnız bilinen değerleri kapsar.
      </v-alert>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :options="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        item-key="id"
        must-sort
        class="admin-data-table"
        @update:options="changeOptions"
      >
        <template #no-data>
          {{ error ? 'Conversionlar yüklenemedi.' : 'Bu filtrelerle conversion bulunamadı.' }}
        </template>
        <template v-slot:item.user="{ item }">
          <div class="conversion-cell">
            <strong>{{ item.userName || 'Kullanıcı eşleşmesi yok' }}</strong><small>{{ item.userId ? `#${item.userId} · ` : '' }}{{ item.email || '-' }}</small>
          </div>
        </template>
        <template v-slot:item.linkName="{ item }">
          <div class="conversion-cell">
            <strong>{{ item.linkName || '-' }}</strong><small>{{ item.linkUniqueId || '-' }}</small>
          </div>
        </template>
        <template v-slot:item.identity="{ item }">
          <div class="conversion-cell">
            <span>İşlem: {{ item.transactionId || '-' }}</span><small>Event: {{ item.providerEventId || '-' }}</small><small>Conversion #{{ item.id }}<span v-if="item.adjustment"> · Düzeltme</span></small>
          </div>
        </template>
        <template v-slot:item.saleAmount="{ item }">
          {{ money(item.saleAmount) }}
        </template>
        <template v-slot:item.networkAmount="{ item }">
          {{ money(item.networkAmount) }}
        </template>
        <template v-slot:item.userAmount="{ item }">
          {{ money(item.userAmount) }}
        </template>
        <template v-slot:item.adverportProfit="{ item }">
          <strong :class="{ 'error--text': item.adverportProfit < 0 }">{{ money(item.adverportProfit) }}</strong>
        </template>
        <template v-slot:item.appliedTier="{ item }">
          <div class="conversion-cell">
            <strong>{{ tierLabel(item.appliedTier) }}</strong><small>{{ payoutLabel(item) }}</small>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <div class="conversion-cell">
            <strong>{{ statusLabel(item.status) }}</strong><small>Network: {{ statusLabel(item.observedStatus) }}</small><small v-if="item.earningStatus">Hakediş: {{ statusLabel(item.earningStatus) }}</small><small v-if="item.userVisible === false">Kullanıcıda gizli</small><small
              v-if="item.allocationState !== 'MATCHED'"
              class="warning--text"
            >{{ allocationLabel(item.allocationState) }}</small>
          </div>
        </template>
        <template v-slot:item.eventDate="{ item }">
          {{ date(item.eventDate) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import { getAccessToken } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'

const emptyFilters = () => ({
  search: '', network: '', status: 'CURRENT', observedStatus: '', tier: '', from: '', to: '', hideZeroNetworkAmount: false,
})

export default {
  components: { AdminPageHeader, AdminMetricStrip },
  data: () => ({
    filters: emptyFilters(),
    applied: {},
    rows: [],
    total: 0,
    summary: {},
    loading: false,
    error: '',
    request: 0,
    cancel: null,
    options: {
      page: 1, itemsPerPage: 25, sortBy: ['eventDate'], sortDesc: [true],
    },
    networks: ['REKLAMACTION', 'GELIRORTAKLARI'],
    statuses: [{ text: 'Bekleyen + kesinleşen', value: 'CURRENT' }, { text: 'Bekleyen', value: 'EXPECTED' }, { text: 'Kesinleşen', value: 'CONFIRMED' }, { text: 'İptal', value: 'CANCELLED' }],
    networkStatuses: [{ text: 'Bekleyen', value: 'PENDING' }, { text: 'Onaylı', value: 'APPROVED' }, { text: 'Reddedildi', value: 'REJECTED' }, { text: 'Bilinmiyor', value: 'UNKNOWN' }],
    tiers: [{ text: 'Premium', value: 'PREMIUM' }, { text: 'Freemium', value: 'FREEMIUM' }, { text: 'Tarihsel paket bilinmiyor', value: 'UNKNOWN' }],
    headers: [
      {
        text: 'Kullanıcı', value: 'user', sortable: false, width: 200,
      },
      {
        text: 'Link adı', value: 'linkName', sortable: false, width: 200,
      },
      {
        text: 'Network', value: 'networkSource', sortable: false, width: 140,
      },
      {
        text: 'İşlem / Event ID', value: 'identity', sortable: false, width: 230,
      },
      {
        text: 'Satış tutarı', value: 'saleAmount', align: 'end', width: 130,
      },
      {
        text: 'Brüt network tutarı', value: 'networkAmount', align: 'end', width: 145,
      },
      {
        text: 'Kullanıcı hakedişi', value: 'userAmount', align: 'end', width: 140,
      },
      {
        text: 'Adverport kârı', value: 'adverportProfit', align: 'end', width: 135,
      },
      {
        text: 'Paket / hakediş oranı', value: 'appliedTier', sortable: false, width: 170,
      },
      {
        text: 'Durum', value: 'status', sortable: false, width: 160,
      },
      { text: 'Conversion tarihi', value: 'eventDate', width: 165 },
    ],
  }),
  computed: {
    summaryCards() {
      return [
        { label: 'Conversion', value: this.formatNumber(this.summary.conversionCount), helper: 'Bekleyen + kesinleşen' },
        { label: 'Brüt network', value: this.money(this.summary.networkAmount), helper: 'İptaller hariç network payout toplamı' },
        { label: 'Kullanıcı hakedişi', value: this.money(this.summary.userAmount), helper: 'İptaller hariç tarihsel hakediş' },
        { label: 'Adverport kârı', value: this.money(this.summary.adverportProfit), helper: 'İptaller hariç; vergi/gider öncesi' },
      ]
    },
  },
  mounted() { this.fetchRows() },
  beforeDestroy() { this.request += 1; if (this.cancel) this.cancel.cancel() },
  methods: {
    applyFilters() {
      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        this.error = 'Başlangıç tarihi bitişten sonra olamaz.'

        return undefined
      }
      this.applied = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value != null && value !== ''))
      this.options = { ...this.options, page: 1 }

      return this.fetchRows()
    },
    resetFilters() {
      this.filters = emptyFilters()

      return this.applyFilters()
    },
    changeOptions(next) {
      const sortBy = next.sortBy?.[0] || 'eventDate'
      const sortDesc = next.sortDesc?.[0] !== false
      const size = [25, 50, 100].includes(next.itemsPerPage) ? next.itemsPerPage : 25
      const reordered = sortBy !== this.options.sortBy[0] || sortDesc !== this.options.sortDesc[0] || size !== this.options.itemsPerPage
      const page = reordered ? 1 : next.page
      if (!reordered && page === this.options.page) return undefined
      this.options = {
        page, itemsPerPage: size, sortBy: [sortBy], sortDesc: [sortDesc],
      }

      return this.fetchRows()
    },
    async fetchRows() {
      this.request += 1
      const { request } = this
      if (this.cancel) this.cancel.cancel()
      this.cancel = axios.CancelToken.source()
      this.loading = true; this.error = ''; this.rows = []; this.summary = {}
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/conversions`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          params: {
            ...this.applied, page: this.options.page - 1, size: this.options.itemsPerPage, sortBy: this.options.sortBy[0], descending: this.options.sortDesc[0],
          },
          cancelToken: this.cancel.token,
          timeout: 30000,
        })
        if (request !== this.request) return
        const result = response?.data?.data
        if (!Array.isArray(result?.items) || !Number.isFinite(result?.totalElements) || !result.summary) throw new Error('Invalid conversion page')
        this.rows = result.items; this.total = result.totalElements; this.summary = result.summary
        this.options = { ...this.options, page: result.number + 1 }
      } catch (error) {
        if (request !== this.request || axios.isCancel(error)) return
        this.error = resolveApiErrorMessage(error, 'Conversionlar yüklenemedi.')
      } finally { if (request === this.request) this.loading = false }
    },
    money(value) {
      if (value == null || value === '') return '-'
      const number = Number(value)

      return Number.isFinite(number) ? new Intl.NumberFormat('tr-TR', {
        style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(number) : '-'
    },
    formatNumber(value) { return new Intl.NumberFormat('tr-TR').format(Number(value || 0)) },
    tierLabel(value) { return { PREMIUM: 'Premium', FREEMIUM: 'Freemium' }[value] || 'Tarihsel paket bilinmiyor' },
    payoutLabel(item) {
      if (item.appliedPayout == null) return 'Tarihsel oran bilinmiyor'
      const type = String(item.appliedPayoutType || '').toLowerCase()
      if (type === 'cpa_percentage') return `%${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 4 }).format(item.appliedPayout)} satış oranı`
      if (type === 'cpa_fixed') return `${this.money(item.appliedPayout)} sabit payout`

      return `${item.appliedPayout} (${item.appliedPayoutType || 'tip bilinmiyor'})`
    },
    statusLabel(value) {
      return {
        EXPECTED: 'Bekleyen', CONFIRMED: 'Kesinleşen', CANCELLED: 'İptal', PENDING: 'Bekleyen', APPROVED: 'Onaylı', REJECTED: 'Reddedildi', UNKNOWN: 'Bilinmiyor',
      }[value] || value || '-'
    },
    allocationLabel(value) { return { MULTIPLE_EARNINGS: 'Çoklu hakediş eşleşmesi', NO_EARNING: 'Hakediş kaydı yok', MISSING_NETWORK_AMOUNT: 'Network tutarı eksik' }[value] || 'Dağılım bilinmiyor' },
    date(value) {
      if (!value) return '-'
      const parsed = new Date(value)

      return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', dateStyle: 'short', timeStyle: 'short' })
    },
  },
}
</script>

<style scoped>
.conversion-cell { display: flex; flex-direction: column; gap: 4px; padding: 10px 0; overflow-wrap: anywhere; }
.conversion-cell small { color: rgba(15, 23, 42, 0.6); }
.conversion-reports ::v-deep td { font-variant-numeric: tabular-nums; }
</style>
